import React from 'react';
import { Shield, Truck, RotateCcw, Lock, Phone, Leaf, Award, CreditCard } from 'lucide-react';
import { SectionShell } from '../primitives/SectionShell';
import { SectionBackground } from '../SectionBackground';
import { SectionHeader } from '../primitives/SectionHeader';
import type { SectionBaseConfig } from '../../../storefront-schema/types';
import { readableTextColor } from '../../../utils/readableTextColor';
import { Editable } from '../inline/Editable';

export type TrustBadgeIcon =
  | 'shipping'
  | 'returns'
  | 'secure'
  | 'support'
  | 'eco'
  | 'quality'
  | 'guarantee'
  | 'payment';

export interface TrustBadgeItem {
  id: string;
  icon: TrustBadgeIcon;
  title: string;
  subtitle?: string;
}

export interface BnplProvider {
  id: string;
  name: 'klarna' | 'afterpay' | 'atome' | 'shopback' | 'paylater' | 'grabpay' | 'custom';
  label?: string;
  logoUrl?: string;
  installments?: number;
}

export interface TrustBadgesConfig extends SectionBaseConfig {
  title?: string;
  subtitle?: string;
  badges?: TrustBadgeItem[];
  showBnpl?: boolean;
  bnplLabel?: string;
  bnplProviders?: BnplProvider[];
  layout?: 'row' | 'grid';
}

const iconMap: Record<TrustBadgeIcon, React.ComponentType<{ className?: string }>> = {
  shipping: Truck,
  returns: RotateCcw,
  secure: Lock,
  support: Phone,
  eco: Leaf,
  quality: Award,
  guarantee: Shield,
  payment: CreditCard,
};

const bnplDefaults: Record<BnplProvider['name'], { label: string; color: string }> = {
  klarna: { label: 'Klarna', color: '#ffa8cd' },
  afterpay: { label: 'Afterpay', color: '#b2fce4' },
  atome: { label: 'Atome', color: '#111111' },
  shopback: { label: 'ShopBack PayLater', color: '#ff5a5f' },
  paylater: { label: 'Pay Later', color: '#333333' },
  grabpay: { label: 'GrabPay PayLater', color: '#00b14f' },
  custom: { label: 'Custom', color: '#888888' },
};

export const TrustBadgesSection: React.FC<{ config?: TrustBadgesConfig }> = ({ config }) => {
  // Accept both seed shapes: canonical `badges` and the legacy `items` key
  // written by inject-trust-badges.sql, plus `label` as an alias for `title`
  // (neon seed uses `label`). Normalize to { id, icon, title, subtitle }.
  const rawBadges = config?.badges?.length
    ? config.badges
    : (config as any)?.items?.length
    ? ((config as any).items as any[])
    : null;
  const badges = rawBadges
    ? rawBadges.map((b: any, i: number) => ({
        id: b.id ?? String(i + 1),
        icon: (b.icon ?? 'guarantee') as TrustBadgeIcon,
        title: b.title ?? b.label ?? '',
        subtitle: b.subtitle,
      }))
    : [
        { id: '1', icon: 'shipping' as const, title: 'Free shipping', subtitle: 'On orders over RM100' },
        { id: '2', icon: 'returns' as const, title: '30-day returns', subtitle: 'Hassle-free' },
        { id: '3', icon: 'secure' as const, title: 'Secure checkout', subtitle: 'SSL encrypted' },
        { id: '4', icon: 'support' as const, title: '24/7 support', subtitle: 'Real humans' },
      ];

  const badgeKey = (config?.badges?.length ? 'badges' : (config as any)?.items?.length ? 'items' : 'badges');

  // ── Vital: "Our standards are higher" — monochrome dark checklist of
  // performance/quality standards (kaged.com register). Big condensed heading +
  // a 2-col grid of ticked standards. ─────────────────────────────────────────
  if ((config as any)?.styleVariant === 'vital') {
    const vBg = config?.colors?.background || 'var(--color-background, #0A0A0B)';
    const vInk = config?.colors?.text || 'var(--color-text, #F2F2F3)';
    const vAccent = config?.colors?.primary || 'var(--color-primary, #FFFFFF)';
    const vMuted = `color-mix(in srgb, ${vInk} 60%, transparent)`;
    const vRule = `color-mix(in srgb, ${vInk} 12%, transparent)`;
    const heading = config?.title || 'Our standards are higher';
    const sub = config?.subtitle || 'Fully dosed. Third-party tested. No proprietary blends, ever.';
    const items = badges && badges.length ? badges : [
      { id: '1', icon: 'quality' as const, title: 'Fully dosed', subtitle: 'Clinical doses on every label — no underdosed fillers.' },
      { id: '2', icon: 'secure' as const, title: 'Third-party tested', subtitle: 'Independently verified for purity and potency.' },
      { id: '3', icon: 'quality' as const, title: 'No proprietary blends', subtitle: 'Every ingredient and amount is disclosed.' },
      { id: '4', icon: 'eco' as const, title: 'Banned-substance free', subtitle: 'Informed-Sport style screening you can trust.' },
    ];
    return (
      <section style={{ background: vBg, color: vInk, padding: 'clamp(56px, 9vw, 110px) 24px', position: 'relative', overflow: 'hidden' }}>
        <SectionBackground config={config} />
        <div style={{ position: 'relative', zIndex: 3, maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ maxWidth: '640px', marginBottom: 'clamp(36px, 5vw, 56px)' }}>
            <h2 style={{ fontFamily: 'var(--font-display, "Anton", system-ui, sans-serif)', fontWeight: 400, fontSize: 'clamp(2rem, 5vw, 3.75rem)', lineHeight: 1.0, letterSpacing: '0.01em', textTransform: 'uppercase', margin: 0, color: vInk }}>
              <Editable as="span" field="config.title" value={heading} />
            </h2>
            {sub ? <p style={{ fontFamily: 'var(--font-body)', fontSize: 'clamp(1rem, 1.3vw, 1.15rem)', lineHeight: 1.6, color: vMuted, marginTop: 18 }}><Editable as="span" field="config.subtitle" value={sub} /></p> : null}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1px', background: vRule, border: `1px solid ${vRule}` }}>
            {items.map((b: any, i: number) => (
              <div key={b.id} style={{ background: vBg, padding: 'clamp(24px, 3vw, 34px)', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <span aria-hidden="true" style={{ flexShrink: 0, width: 26, height: 26, borderRadius: '50%', border: `1.5px solid ${vAccent}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: 2 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={vAccent} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                </span>
                <div>
                  <div style={{ fontFamily: 'var(--font-heading)', fontSize: '1.05rem', fontWeight: 700, letterSpacing: '0.01em', color: vInk }}><Editable as="span" field={`config.${badgeKey}.${i}.title`} value={b.title} /></div>
                  {b.subtitle ? <div style={{ fontFamily: 'var(--font-body)', fontSize: '0.9rem', lineHeight: 1.55, color: vMuted, marginTop: 6 }}><Editable as="span" field={`config.${badgeKey}.${i}.subtitle`} value={b.subtitle} /></div> : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // ── Glam: editorial "Why Glam" brand-story block (full-bleed wine panel +
  // big photo + award-style badges) — the premium beauty register, not a row
  // of utility chips. ───────────────────────────────────────────────────────
  if ((config as any)?.styleVariant === 'glam') {
    const glBg = config?.colors?.background || '#5A1E2D';
    const glText = config?.colors?.text || '#FBEEF0';
    const glPink = config?.colors?.primary || 'var(--color-primary, #EC4899)';
    const glMuted = `color-mix(in srgb, ${glText} 72%, transparent)`;
    const glImage =
      (config as any)?.image ||
      (config as any)?.imageUrl ||
      (config as any)?.backgroundImage ||
      'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?auto=format&fit=crop&w=1100&q=80';
    const heading = config?.title || 'Why Glam';
    const body =
      config?.subtitle ||
      'We simplify everyday beauty with reusable, salon-quality looks — designed to be worn, loved, and reworn. No appointments, no mess, no compromise.';
    return (
      <section className="-mx-4 overflow-hidden" style={{ background: glBg, color: glText }}>
        <div className="max-w-screen-2xl mx-auto grid lg:grid-cols-2 items-stretch">
          <div
            className="flex flex-col justify-center"
            style={{ padding: 'clamp(40px, 6vw, 84px)' }}
          >
            <span
              style={{
                fontFamily: "var(--font-body, 'Manrope', sans-serif)",
                fontSize: '0.72rem', fontWeight: 600,
                letterSpacing: '0.2em', textTransform: 'uppercase',
                color: glPink, marginBottom: 18,
              }}
            >
              The Glam difference
            </span>
            <h2
              style={{
                fontFamily: "var(--font-heading, 'Poppins', sans-serif)",
                fontSize: 'clamp(2.1rem, 4.6vw, 3.4rem)',
                fontWeight: 700, lineHeight: 1.04, letterSpacing: '-0.02em',
                margin: 0, color: glText,
              }}
            >
              <Editable as="span" field="config.title" value={heading} />
            </h2>
            <p
              style={{
                fontFamily: "var(--font-body, 'Manrope', sans-serif)",
                fontSize: 'clamp(1rem, 1.3vw, 1.12rem)', lineHeight: 1.65,
                color: glMuted, marginTop: 20, maxWidth: '46ch',
              }}
            >
              <Editable as="span" field="config.subtitle" value={body} />
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5" style={{ marginTop: 'clamp(28px, 4vw, 44px)' }}>
              {badges.map((b, i) => {
                const Icon = iconMap[b.icon] || Award;
                return (
                  <div key={b.id} className="flex items-center gap-3">
                    <span
                      className="flex items-center justify-center flex-shrink-0"
                      style={{
                        width: 44, height: 44, borderRadius: '50%',
                        border: `1px solid color-mix(in srgb, ${glPink} 55%, transparent)`,
                        color: glPink,
                      }}
                    >
                      <Icon className="w-5 h-5" />
                    </span>
                    <span
                      style={{
                        fontFamily: "var(--font-body, 'Manrope', sans-serif)",
                        fontSize: '0.92rem', fontWeight: 600, lineHeight: 1.25,
                        color: glText,
                      }}
                    >
                      <Editable as="span" field={`config.${badgeKey}.${i}.title`} value={b.title} />
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <div
            aria-hidden="true"
            className="relative min-h-[300px] lg:min-h-[520px]"
            style={{
              backgroundImage: `url(${glImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        </div>
      </section>
    );
  }

  const layout = config?.layout || 'row';
  const gridClass =
    layout === 'grid'
      ? 'grid grid-cols-2 md:grid-cols-4 gap-4'
      : 'flex flex-wrap gap-4 justify-center';

  const bnplProviders = config?.bnplProviders?.length
    ? config.bnplProviders
    : [
        { id: 'k', name: 'atome' as const, installments: 3 },
        { id: 'gb', name: 'grabpay' as const, installments: 4 },
        { id: 'sp', name: 'shopback' as const, installments: 4 },
      ];

  return (
    <SectionShell config={config}>
      {(config?.title || config?.subtitle) && (
        <SectionHeader title={config?.title} subtitle={config?.subtitle} />
      )}
      <div className={gridClass}>
        {badges.map((b, i) => {
          const Icon = iconMap[b.icon] || Shield;
          return (
            <div
              key={b.id}
              className={`flex items-center gap-3 p-4 rounded-2xl flex-1 ${layout === 'grid' ? '' : 'min-w-[200px]'}`}
              style={{ background: 'var(--glass-bg, rgba(255,255,255,0.7))' }}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'color-mix(in srgb, var(--primary-color, #007AFF) 12%, transparent)' }}
              >
                <Icon className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate"><Editable as="span" field={`config.${badgeKey}.${i}.title`} value={b.title} /></p>
                {b.subtitle && <p className="text-xs opacity-70 truncate"><Editable as="span" field={`config.${badgeKey}.${i}.subtitle`} value={b.subtitle} /></p>}
              </div>
            </div>
          );
        })}
      </div>

      {config?.showBnpl !== false && (
        <div className="mt-6 text-center">
          <p className="text-xs uppercase tracking-wider opacity-60 mb-3">
            <Editable as="span" field="config.bnplLabel" value={config?.bnplLabel || 'Buy now, pay later with'} />
          </p>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {bnplProviders.map((p, i) => {
              const meta = bnplDefaults[p.name] || bnplDefaults.custom;
              const label = p.label || meta.label;
              return (
                <div
                  key={p.id}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold"
                  style={{ background: meta.color, color: readableTextColor(meta.color) }}
                >
                  {p.logoUrl && <img loading="lazy" decoding="async" src={p.logoUrl} alt="" className="h-4" />}
                  <span><Editable as="span" field={`config.bnplProviders.${i}.label`} value={label} /></span>
                  {p.installments && <span>· {p.installments}×</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </SectionShell>
  );
};

export default TrustBadgesSection;
